/* eslint-disable react/no-multi-comp */
import moment from 'moment'
import React, { useMemo } from 'react'

import KeyValues, { KeyValue } from 'components/KeyValues'
import NavigatableIntegrationWidgetCard from 'components/integrations/NavigatableIntegrationWidgetCard'
import { toTimePeriod, getUnixTimestampDisplayString } from 'util/date'
import { getFormattedPrice } from 'util/numbers'

import Label from './Label'
import { getStatusProps } from './StripeChargeRow'

const statusProps = {
  trialing: { textColor: 'groovy', text: 'Trial' },
  active: { textColor: 'groovy', text: 'Active' },
  past_due: { textColor: 'berry', text: 'Past due' },
  canceled: { textColor: 'berry', text: 'Cancelled' },
  unpaid: { textColor: 'berry', text: 'Unpaid' },
}

export default function StripeDetail({
  className,
  data: { charges, subscriptions },
  doNavigatableClearState,
  navigatableState,
}) {
  const { id, type } = navigatableState || {}
  const subscription = useMemo(
    () => {
      return (
        subscriptions &&
        subscriptions.data &&
        subscriptions.data.find(({ id: subId }) => id === subId)
      )
    },
    [subscriptions, id]
  )
  const charge = useMemo(
    () => {
      return (
        charges &&
        charges.data &&
        charges.data.find(({ id: subId }) => id === subId)
      )
    },
    [charges, id]
  )
  if (type === 'subscription') {
    const {
      cancel_at_period_end: cancelAtPeriodEnd,
      created,
      current_period_end: currentPeriodEnd,
      discount,
      plan: {
        interval,
        interval_count: intervalCount,
        product: { name: productName } = {},
        nickname,
      } = {},

      quantity,
    } =
      subscription || {}
    return (
      <NavigatableIntegrationWidgetCard.Detail
        className={className}
        onBack={doNavigatableClearState}
        title={productName || nickname}
      >
        <KeyValues>
          {id && (
            <KeyValue
              title="ID"
              value={
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://dashboard.stripe.com/subscriptions/${id}`}
                >
                  {id}
                </a>
              }
            />
          )}
          {created && (
            <KeyValue
              title="Subscribed"
              value={getUnixTimestampDisplayString(created)}
            />
          )}
          {status && (
            <KeyValue
              {...{
                ...statusProps[status],
                title: 'Status',
                value: status,
              }}
            />
          )}
          {nickname && <KeyValue title="Plan name" value={nickname} />}
          {interval && (
            <KeyValue
              title="Billing cycle"
              value={toTimePeriod(interval, intervalCount)}
            />
          )}
          {discount && <KeyValue title="Discount" value={discount} />}
          {quantity && <KeyValue title="Number of plans" value={quantity} />}
          {currentPeriodEnd && (
            <KeyValue
              title="Next bill date"
              value={getUnixTimestampDisplayString(currentPeriodEnd)}
            />
          )}
          <KeyValue title="Set to cancel" value={`${!!cancelAtPeriodEnd}`} />
        </KeyValues>
      </NavigatableIntegrationWidgetCard.Detail>
    )
  }
  if (charge) {
    const { amount, currency } = charge || {}
    return (
      <NavigatableIntegrationWidgetCard.Detail
        className={className}
        onBack={doNavigatableClearState}
        title={
          <>
            <Label {...getStatusProps(charge)} />
            {' · '}
            {getFormattedPrice(amount, currency)}
          </>
        }
      >
        <KeyValues>
          <KeyValue
            title="Status"
            value={<Label {...getStatusProps(charge)} />}
          />
          <KeyValue
            title="Amount"
            value={getFormattedPrice(charge.amount, charge.currency)}
          />
          <KeyValue
            title="Date"
            value={moment.unix(charge.created).format('Do MMM YYYY')}
          />
        </KeyValues>
      </NavigatableIntegrationWidgetCard.Detail>
    )
  }
  return null
}
